import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Custom hook to reset scroll position of specific elements on route change.
 * 
 * In this application, the authenticated layouts use position: fixed, meaning the
 * document body itself never scrolls. Instead, inner elements (often due to 
 * accidental or intentional overflow auto/scroll) become the scroll containers.
 * `window.scrollTo(0,0)` will not work for these inner containers.
 */
export function useScrollToTopOnNavigate(
  ...refs: Array<React.RefObject<HTMLElement | null>>
) {
  const location = useLocation();
  
  // Store refs in a mutable ref object to avoid triggering the effect 
  // on every render if the array reference changes.
  const refsToScroll = useRef(refs);
  refsToScroll.current = refs;

  useLayoutEffect(() => {
    // Do not reset scroll if the user is navigating to an anchor hash on the same page.
    // The globally mounted ScrollToHashElement will handle the scrolling in this case.
    if (location.hash) {
      return;
    }

    refsToScroll.current.forEach((ref) => {
      if (ref.current && ref.current.scrollTop !== 0) {
        // Direct assignment is used for an instant jump, bypassing any smooth scrolling.
        // The project doesn't have `scroll-behavior: smooth` globally, but this ensures
        // the reset is immediate.
        ref.current.scrollTop = 0;
      }
    });
  }, [location.pathname]); // Depend ONLY on pathname. 
  // Resetting on search params would break UI when filters, tabs, or pagination change.
}